import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { CommandService } from "../../services/Command.service";
import {
  CommandState,
  getCommandStateValues,
} from "../../models/commandState.enum";
import { CommandInfo } from "../../models/commandInfo.model";
import { first, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { Area } from "src/app/shared/Models/read/Area";
import { BuildingService } from "src/app/core/building-list/services/building.service";
import { SessionService } from "src/app/core/survey/shared/services/session.service";
import { MultiSelectItem } from "primeng/multiselect";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";
import { SelectItem } from "primeng/api";
import { ActivatedRoute, Router } from "@angular/router";
import { DeviceCheckService } from "src/app/core/survey/shared/services/device-check.service";
import { AreaService } from "src/app/shared/services/area.service";

@Component({
  selector: "app-failed-command",
  templateUrl: "./failed-command.component.html",
  styleUrls: ["./failed-command.component.scss"],
})
export class FailedCommandComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @ViewChild("dt", { static: false }) commandsTable: any;

  options: MultiSelectItem;
  commandState = CommandState;
  filterStatesCriteria = getCommandStateValues();
  filterTypesCriteria = [];
  commandStates = getCommandStateValues().map(
    (x) => ({ label: CommandState[x], value: x } as SelectItem)
  );
  commandTypes: SelectItem[] = [];
  fromDateValue: Date = null;
  tillDateValue: Date = null;
  commands: CommandInfo[] = [];
  addressFilterText: string;
  cols: any[];
  cities: string[] = [];
  fiberhoods: Area[] = [];
  blockList: Area[] = [];
  searching = false;

  selectedCity: string = null;
  selectedFiberhood: Area = null;
  selectedBlock: Area = null;

  load: boolean = false;

  scrollOffsetLandscape: number = 190;
  scrollOffsetPortrait: number = 190;

  skipPreValidation = false;

  constructor(
    private commandService: CommandService,
    private buildingService: BuildingService,
    private sessionService: SessionService,
    private permissionService: PermissionsService,
    private router: Router,
    private route: ActivatedRoute,
    private deviceCheckService: DeviceCheckService,
    private areaService: AreaService
  ) {
    this.route.queryParams.subscribe((params) => {
      this.load = params.load;
    });

    this.clearFilter();

    this.getCities();
    this.cols = [
      {
        field: "Title",
        header: "Command",
        class: "title-column",
      },
      {
        field: "CommandAddress.BuildingLomKey",
        header: "BID",
        class: "building-key-column",
      },
      {
        field: "CommandAddress.LomKey",
        header: "GAID",
        class: "address-key-column",
      },
      {
        field: "Zoning",
        header: "Zoning",
        class: "zoning-column",
      },
      {
        field: "CommandAddress.FullAddress",
        header: "Address",
        class: "address-column",
      },
      {
        field: "LastAttempt.StartDate",
        header: "Started On",
        class: "started-on-column",
      },
      { field: "State", header: "State", class: "state-column" },
      { header: "", class: "attempts-column" },
    ];
  }

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.getSessionStorageData();
    this.getCities();
    this.getCommandTypes();
    if (!this.selectedCity) return;
    this.getFiberhoods();
    if (!this.selectedFiberhood) return;
    this.getBlockList(this.selectedFiberhood.id);
  }

  ngAfterViewInit(): void {
    if (this.load) this.exectuteCommandQuery();
  }

  clearFilter(clearList: boolean = false) {
    this.fiberhoods = [];
    this.blockList = [];

    this.selectedCity = null;
    this.selectedFiberhood = null;
    this.selectedBlock = null;

    this.addressFilterText = "";

    this.fromDateValue = null;
    this.tillDateValue = null;

    this.filterStatesCriteria = [];
    this.filterTypesCriteria = [];
    if (clearList) {
      this.commands = [];
      this.clearSessionStorageData();
    }
  }

  checkIfValid(): boolean {
    if (
      !!this.selectedFiberhood &&
      !!this.fromDateValue &&
      !!this.tillDateValue
    )
      return true;
    if (
      this.filterTypesCriteria.length > 0 &&
      !!this.fromDateValue &&
      !!this.tillDateValue
    )
      return true;
    if (this.addressFilterText) return true;

    return false;
  }

  exectuteCommandQuery() {
    if (!this.checkIfValid()) return;

    this.searching = true;

    var data = {
      from: this.fromDateValue?.toUTCString(),
      till: this.tillDateValue?.toUTCString(),
      fiberHoodId: this.selectedFiberhood?.id,
      areaId: this.selectedBlock?.id,
      addressFilter: this.addressFilterText,
      filterTypes: this.filterTypesCriteria,
      filterStates: this.filterStatesCriteria,
    };

    this.setSessionStorageData();

    this.commandService
      .postCommands(data)
      .pipe(first())
      .subscribe((res: CommandInfo[]) => {
        this.searching = false;
        this.commands = res;
      });
  }

  retryCommand(id: string) {
    this.commandService.retryCommand(id).subscribe((res) => {
      this.exectuteCommandQuery();
    });
  }

  restartCommand(id: string) {
    this.commandService
      .restartCommand(id, this.skipPreValidation)
      .subscribe((res) => {
        this.exectuteCommandQuery();
      });
  }

  onToggleCollapse(parent: { Collapsed: boolean }) {
    parent.Collapsed = !parent.Collapsed;
  }

  onSelectCommandDetails(id) {
    this.router.navigate(["/admin/commands/", id]);
  }

  refreshData() {
    // Sometimes, the paging was not correct when searching. So reset it before binding the data to the table.
    this.commandsTable._first = 0;
    this.exectuteCommandQuery();
  }

  getSessionStorageData() {
    const fromDate = sessionStorage.getItem("fromDate");
    const tillDate = sessionStorage.getItem("tillDate");
    this.fromDateValue = fromDate ? new Date(fromDate) : null;
    this.tillDateValue = tillDate ? new Date(tillDate) : null;
    const city = sessionStorage.getItem("city");
    if (city) this.selectedCity = city;
    this.selectedFiberhood = this.sessionService.getFiberhood();
    this.selectedBlock = this.sessionService.getBlock();
    this.addressFilterText = sessionStorage.getItem("addressFilterText") ?? "";
    this.filterTypesCriteria =
      JSON.parse(sessionStorage.getItem("filterTypesCriteria")) ?? [];
    this.filterStatesCriteria =
      JSON.parse(sessionStorage.getItem("filterStatesCriteria")) ?? [];
  }

  setSessionStorageData() {
    sessionStorage.setItem(
      "fromDate",
      this.fromDateValue ? this.fromDateValue.toUTCString() : ""
    );
    sessionStorage.setItem(
      "tillDate",
      this.tillDateValue ? this.tillDateValue.toUTCString() : ""
    );
    sessionStorage.setItem("addressFilterText", this.addressFilterText ?? "");
    sessionStorage.setItem("city", this.selectedCity ?? "");
    sessionStorage.setItem("block", JSON.stringify(this.selectedBlock));
    sessionStorage.setItem("fiberhood", JSON.stringify(this.selectedFiberhood));
    if (this.selectedBlock != null)
      sessionStorage.setItem("areaId", this.selectedBlock.id);
    else if (this.selectedFiberhood != null)
      sessionStorage.setItem("areaId", this.selectedFiberhood.id);
    else sessionStorage.removeItem("areaId");

    sessionStorage.setItem(
      "filterTypesCriteria",
      JSON.stringify(this.filterTypesCriteria)
    );
    sessionStorage.setItem(
      "filterStatesCriteria",
      JSON.stringify(this.filterStatesCriteria)
    );
  }

  clearSessionStorageData() {
    sessionStorage.removeItem("fromDate");
    sessionStorage.removeItem("tillDate");
    sessionStorage.removeItem("city");
    sessionStorage.removeItem("block");
    sessionStorage.removeItem("fiberhood");
    sessionStorage.removeItem("areaId");
    sessionStorage.removeItem("filterTypesCriteria");
    sessionStorage.removeItem("filterStatesCriteria");
    sessionStorage.removeItem("addressFilterText");
  }

  getCommandTypes() {
    this.buildingService
      .getCommandTypeList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((CommandTypeList: string[]) => {
        this.commandTypes = CommandTypeList.map(
          (x) => ({ label: x, value: x } as SelectItem)
        );
      });
  }

  getCities() {
    this.areaService
      .getCityNames()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: string[]) => {
        this.cities = res;
      });
  }

  getFiberhoods() {
    this.areaService
      .getFiberHoodsForCityName(this.selectedCity)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: Area[]) => {
        this.fiberhoods = res;
      });
  }

  getBlockList(parentId: string) {
    this.areaService
      .getBlocksForFiberHoodId(parentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: Area[]) => {
        this.blockList = res;
      });
  }

  citySelected(city: string) {
    if (city === this.selectedCity) return;

    this.selectedCity = city;
    this.selectedFiberhood = null;
    this.selectedBlock = null;

    sessionStorage.setItem("city", this.selectedCity);
    this.getFiberhoods();
  }

  clearCity() {
    sessionStorage.removeItem("city");
    sessionStorage.removeItem("fiberhood");
    sessionStorage.removeItem("block");
    sessionStorage.removeItem("areaId");

    this.selectedCity = null;
    this.selectedFiberhood = null;
    this.selectedBlock = null;
    this.fiberhoods = [];
    this.blockList = [];
  }

  fiberHoodSelected(fiberHood: Area) {
    if (fiberHood.id === this.selectedFiberhood?.id) return null;

    this.selectedFiberhood = fiberHood;

    this.selectedBlock = null;
    sessionStorage.setItem("block", null);
    sessionStorage.setItem("fiberhood", JSON.stringify(this.selectedFiberhood));

    this.selectedBlock = null;
    sessionStorage.setItem("areaId", fiberHood.id); // saving areaId
    this.getBlockList(fiberHood.id);
  }

  clearFiberHood() {
    sessionStorage.removeItem("fiberhood");
    sessionStorage.removeItem("block");
    sessionStorage.removeItem("areaId");

    this.selectedFiberhood = null;
    this.selectedBlock = null;
    this.blockList = [];
  }

  blockSelected(block: Area) {
    if (this.selectedBlock !== null && block.id === this.selectedBlock.id)
      return;

    this.selectedBlock = block;
    sessionStorage.setItem("areaId", this.selectedBlock.id); // saving areaId
    sessionStorage.setItem("block", JSON.stringify(this.selectedBlock));
  }

  clearBlock() {
    sessionStorage.removeItem("block");
    sessionStorage.removeItem("areaId");
    sessionStorage.setItem("fiberhood", JSON.stringify(this.selectedFiberhood));
    this.selectedBlock = null;
  }

  get fromDate() {
    return this.fromDateValue;
  }
  set fromDate(value: Date) {
    if (this.fromDateValue === value) return;
    if (value) sessionStorage.setItem("fromDate", value?.toUTCString());
    if (!value) sessionStorage.removeItem("fromDate");

    this.fromDateValue = value;
  }

  get tillDate() {
    return this.tillDateValue;
  }

  set tillDate(value: Date) {
    if (this.tillDateValue === value) return;
    if (value) sessionStorage.setItem("tillDate", value?.toUTCString());
    if (!value) sessionStorage.removeItem("tillDate");

    this.tillDateValue = value;
  }

  canRetryCommand(): boolean {
    return this.permissionService.allowed(PermissionsEnum.RetryCommand);
  }

  canRestartCommand(): boolean {
    return this.permissionService.allowed(PermissionsEnum.RestartCommand);
  }

  getScrollHeight() {
    if (!this.deviceCheckService.widthCheck(1000))
      return window.innerHeight - this.scrollOffsetLandscape + "px";
    else return window.innerHeight - this.scrollOffsetPortrait + "px";
  }
}
